import { useState, useEffect } from 'react';
import { updateOrderStatus, updateOrder, getActiveOrders, addMessageToOrder, markMessagesAsRead } from '../utils/storage';
import useNotifications from './useNotifications';
import { useAuth } from '../contexts/AuthContext';
import { resolveUserType } from '../utils/resolveUserType';

export const useOrders = (filters = {}) => {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Intégrer le hook de notifications
    const {
        unreadCounts,
        loadUnreadCounts,
        decrementUnreadCount
    } = useNotifications();

    const loadOrders = async (showSpinner = false) => {
        try {
            if (showSpinner) setLoading(true);
            setError(null);
            const ordersData = await getActiveOrders(filters);
            setOrders(ordersData);

            // Déterminer le userType basé sur les filtres
            const userType = resolveUserType(filters, user?.user_type);

            // Charger les compteurs de messages non lus si on a un userType
            if (userType && ordersData.length > 0) {
                await loadUnreadCounts(ordersData, userType);
            }
        } catch (err) {
            console.error('Error loading orders:', err);
            setError('Erreur lors du chargement des commandes');
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrders(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.prosthetistId, filters.clientId, user?.user_type]);

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await updateOrderStatus(orderId, newStatus);
            // Recharger les commandes pour mettre à jour l'interface
            await loadOrders();
        } catch (err) {
            console.error('Error updating order status:', err);
            setError('Erreur lors de la mise à jour du statut');
        }
    };

    const handleSendMessage = async (orderId, messageData) => {
        try {
            const newMessage = await addMessageToOrder(orderId, messageData);

            // Mettre à jour l'état orders avec le nouveau message
            // pour que les données soient fraîches à la réouverture du modal
            setOrders(prevOrders => prevOrders.map(order => {
                if (order.id === orderId) {
                    return {
                        ...order,
                        discussion: {
                            ...order.discussion,
                            messages: [...(order.discussion?.messages || []), newMessage],
                            lastActivity: newMessage.timestamp
                        }
                    };
                }
                return order;
            }));

            return newMessage;
        } catch (err) {
            console.error('Error sending message:', err);
            setError('Erreur lors de l\'envoi du message');
            throw err;
        }
    };

    // Gérer les messages reçus via WebSocket (d'autres utilisateurs)
    const handleWebSocketMessage = (orderId, message) => {
        setOrders(prevOrders => prevOrders.map(order => {
            if (order.id === orderId) {
                // Éviter les doublons
                const existingIds = new Set(order.discussion?.messages?.map(m => m.id) || []);
                if (existingIds.has(message.id)) {
                    return order;
                }
                return {
                    ...order,
                    discussion: {
                        ...order.discussion,
                        messages: [...(order.discussion?.messages || []), message],
                        lastActivity: message.timestamp
                    }
                };
            }
            return order;
        }));
    };

    const handleMarkMessagesAsRead = async (orderId, userType) => {
        try {
            // Optimistic update : décrémenter immédiatement le badge
            decrementUnreadCount(orderId);

            // Marquer les messages comme lus dans le backend
            await markMessagesAsRead(orderId, userType);

            // Pas besoin de recharger les commandes : l'optimistic update suffit
            // Le prochain polling mettra à jour les données si nécessaire
            return true;
        } catch (err) {
            console.error('Error marking messages as read:', err);
            setError('Erreur lors de la mise à jour des messages');
            // En cas d'erreur, recharger seulement les counts
            const userType = resolveUserType(filters, user?.user_type);
            if (userType && orders.length > 0) {
                await loadUnreadCounts(orders, userType);
            }
            return false;
        }
    };

    const handleUpdateOrder = async (orderId) => {
        try {
            // Recharger les commandes pour récupérer les données mises à jour
            await loadOrders();
        } catch (err) {
            console.error('Error refreshing after order update:', err);
            setError('Erreur lors du rafraîchissement après modification');
        }
    };

    const refetch = () => {
        loadOrders();
    };

    const refreshUnreadCounts = async () => {
        const userType = resolveUserType(filters, user?.user_type);
        if (userType && orders.length > 0) {
            await loadUnreadCounts(orders, userType);
        }
    };

    return {
        orders,
        loading,
        error,
        unreadCounts,
        handleStatusChange,
        handleSendMessage,
        handleMarkMessagesAsRead,
        handleWebSocketMessage,
        handleUpdateOrder,
        refetch,
        refreshUnreadCounts
    };
};